import { difference, intersection } from "lodash-es";
import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { copy } from "shared/assets";
import { Modal } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { getIdFromPath } from "shared/utils";
import { EquipmentForm } from "Venues/components";
import { useStagePageContext, useVenuePageContext } from "Venues/contexts";
import {
  dataMetaSelector,
  dispatchForceGetStageEquipmentAction,
  editEquipment,
} from "Venues/state";
import { IEquipment, IPatchEquipment, VENUE_ACTIONS } from "Venues/types";
import { useClearActionMetaOnUnMount } from "Venues/utils";

const { editEquipment: editEquipmentCopy, toast } = copy;

interface IEditEquipmentModalProps {
  equipment?: IEquipment;
  handleClose: () => void;
}

export const EditEquipmentModal: React.FC<IEditEquipmentModalProps> = ({
  equipment,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const { api: editApiMeta } = useSelector(
    dataMetaSelector(VENUE_ACTIONS.editEquipment)
  );
  const { venueId } = useVenuePageContext();
  const { stageId } = useStagePageContext();
  useClearActionMetaOnUnMount(VENUE_ACTIONS.editEquipment);

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: true,
      secondary: {
        onClick: handleClose,
      },
    }),
    [handleClose]
  );

  const editedEquipmentRef = useRef<IPatchEquipment | null>(null);

  useEffect(() => {
    if (editApiMeta?.status === IAPIState.succeeded) {
      dispatch(
        addTemporaryToast({
          message: toast.changed,
          status: IStatus.success,
        })
      );
      handleClose();

      const editedStages =
        editedEquipmentRef.current?.stages.map((id) =>
          getIdFromPath(id, API_ENDPOINTS.STAGES)
        ) || [];
      const originalStages =
        equipment?.stages.map(({ id }) =>
          getIdFromPath(id, API_ENDPOINTS.STAGES)
        ) || [];

      const unselectedStages = difference(originalStages, editedStages);
      const selectedStages = difference(editedStages, originalStages);
      const keptStages = intersection(originalStages, editedStages);

      // clear pagination and refetch equipment
      // for all unselected stages
      unselectedStages.forEach((stageId) => {
        if (!equipment?.category) return;

        dispatchForceGetStageEquipmentAction(dispatch, {
          venue: venueId,
          stage: stageId,
          category: equipment?.category,
        });
      });

      // clear pagination and refetch equipment
      // for all newly selected stages
      selectedStages.forEach((stageId) => {
        if (!editedEquipmentRef.current?.category) return;

        dispatchForceGetStageEquipmentAction(dispatch, {
          venue: venueId,
          stage: stageId,
          category: editedEquipmentRef.current?.category,
        });
      });

      // clear pagination and refetch equipment
      // for all kept stage where category has changed
      keptStages.forEach((stageId) => {
        const refetchCategory =
          equipment?.category !== editedEquipmentRef.current?.category
            ? editedEquipmentRef.current?.category
            : undefined;

        if (!refetchCategory) return;

        // refetch equipment for new category
        dispatchForceGetStageEquipmentAction(dispatch, {
          venue: venueId,
          stage: stageId,
          category: refetchCategory,
        });

        if (!equipment?.category) return;

        // refetch equipment for old category
        dispatchForceGetStageEquipmentAction(dispatch, {
          venue: venueId,
          stage: stageId,
          category: equipment?.category,
        });
      });
    }
  }, [editApiMeta, dispatch, handleClose, venueId, equipment, stageId]);

  const handleSubmit = (editedEquipment: any) => {
    if (equipment?.id) {
      editedEquipmentRef.current = editedEquipment as IPatchEquipment;

      dispatch(
        editEquipment({
          id: equipment?.id,
          equipment: editedEquipment as IEquipment,
        })
      );
    }
  };

  return (
    <Modal
      handleClose={handleClose}
      title={editEquipmentCopy.title}
      data-testid="edit_equipment_modal"
    >
      <EquipmentForm
        defaultEquipmentValues={equipment}
        buttons={buttons}
        handleSubmit={handleSubmit}
        apiStatus={editApiMeta?.status}
        hasError={!!editApiMeta?.error}
      />
    </Modal>
  );
};
